import { CheckCircle2, AlertTriangle, SlidersHorizontal, Trash2, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { assetUrl } from "@/lib/api";
import { DFC_LAYOUTS, LAYOUTS, RARITIES, VISIBILITIES, colorsFromCost } from "@/lib/mtg";
import { PLAYS_AS } from "@/lib/planar";
import { ColorToggles } from "@/components/common/ColorPips";

const cell = "border-b border-[#2A3447] px-2 py-2 align-middle";
const input = "field px-2 py-1.5 text-xs";

function Thumb({ url, alt }) {
  return url ? <img src={assetUrl(url)} alt={alt} className="h-14 w-14 rounded object-contain" /> : <div className="h-14 w-10 rounded border border-dashed border-red-700/60 bg-red-950/30" />;
}

export function BulkRow({ row, index, update, remove, openDetails }) {
  const face = row.faces[0];
  const setFace = (patch) => update({ faces: row.faces.map((f, i) => (i === 0 ? { ...f, ...patch } : f)) });
  const published = row.status === "published";
  const id = `bulk-row-${index}`;
  return (
    <tr className={`${published ? "opacity-60" : ""} ${row.status === "error" ? "bg-red-950/20" : ""} hover:bg-white/[0.02]`} data-testid={id}>
      <td className={`${cell} sticky left-0 z-10 min-w-[96px] bg-[#121620]`}>
        <div className="flex items-center gap-1">
          <Thumb url={face.image_url} alt={face.name} />
          {DFC_LAYOUTS.includes(row.layout) && <Thumb url={row.faces[1]?.image_url} alt="Back face" />}
        </div>
      </td>
      <td className={cell}><input className={`${input} min-w-[160px]`} value={face.name} onChange={(e) => setFace({ name: e.target.value })} aria-label="Card name" disabled={published} data-testid={`${id}-name`} /></td>
      <td className={cell}><input className={`${input} w-28 font-mono`} placeholder="{1}{R}" value={face.mana_cost} onChange={(e) => setFace({ mana_cost: e.target.value })} aria-label="Mana cost" disabled={published} data-testid={`${id}-mana`} /></td>
      <td className={cell}><input className={`${input} min-w-[170px]`} value={face.type_line} onChange={(e) => setFace({ type_line: e.target.value })} aria-label="Type line" disabled={published} data-testid={`${id}-type`} /></td>
      <td className={cell}>
        <ColorToggles size="sm" value={row.colors ?? colorsFromCost(...row.faces.map((f) => f.mana_cost))} onChange={(v) => update({ colors: v })} testidPrefix={`${id}-color`} />
      </td>
      <td className={cell}>
        <select className={`${input} min-w-[100px] capitalize`} value={row.rarity} onChange={(e) => update({ rarity: e.target.value })} aria-label="Rarity" disabled={published} data-testid={`${id}-rarity`}>
          {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td className={cell}>
        <select className={`${input} min-w-[120px]`} value={row.plays_as || ""} onChange={(e) => update({ plays_as: e.target.value })} aria-label="Plays as" disabled={published} data-testid={`${id}-plays-as`}>
          {PLAYS_AS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </td>
      <td className={cell}>
        <select className={`${input} min-w-[150px]`} value={row.plays_as ? "planar" : row.layout} onChange={(e) => update({ layout: e.target.value })} aria-label="Layout" disabled={published || !!row.plays_as} data-testid={`${id}-layout`}>
          {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </td>
      <td className={cell}><input className={`${input} w-20 uppercase`} maxLength={8} value={row.set_code} onChange={(e) => update({ set_code: e.target.value.toUpperCase() })} aria-label="Set code" disabled={published} data-testid={`${id}-set`} /></td>
      <td className={cell}>
        <select className={`${input} min-w-[100px] capitalize`} value={row.visibility} onChange={(e) => update({ visibility: e.target.value })} aria-label="Visibility" disabled={published} data-testid={`${id}-visibility`}>
          {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </td>
      <td className={`${cell} min-w-[150px]`}>
        {published ? (
          <Link to={`/cards/${row.card_id}`} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:underline" data-testid={`${id}-published-link`}><CheckCircle2 className="h-3.5 w-3.5" />Published</Link>
        ) : row.error ? (
          <span className="inline-flex items-start gap-1 text-xs text-red-300" data-testid={`${id}-error`}><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{row.error}</span>
        ) : (
          <span className="text-xs text-slate-500">Ready</span>
        )}
      </td>
      <td className={`${cell} whitespace-nowrap text-right`}>
        <button className="btn-ghost px-2" onClick={() => openDetails(row.key)} disabled={published} aria-label="Edit details and back face" data-testid={`${id}-details`}>
          {row.faces.length > 1 ? <Layers className="h-4 w-4 text-amber-300" /> : <SlidersHorizontal className="h-4 w-4" />}
        </button>
        <button className="btn-ghost px-2 hover:text-red-300" onClick={() => remove(row.key)} aria-label="Remove row" data-testid={`${id}-remove`}><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

const HEADERS = ["Image", "Name", "Mana cost", "Type line", "Colors", "Rarity", "Plays as", "Layout", "Set", "Visibility", "Status", ""];

export function BulkReviewGrid({ rows, page, pageSize, ...handlers }) {
  const start = (page - 1) * pageSize;
  return (
    <div className="panel max-h-[70vh] overflow-auto" data-testid="bulk-review-grid">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="sticky top-0 z-20 bg-[#1A202C]">
          <tr>
            {HEADERS.map((h, i) => (
              <th key={i} scope="col" className={`border-b border-amber-900/40 px-2 py-3 font-mono text-[10px] uppercase tracking-widest text-amber-300/80 ${i === 0 ? "sticky left-0 z-30 bg-[#1A202C]" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(start, start + pageSize).map((r, i) => (
            <BulkRow key={r.key} row={r} index={start + i} update={handlers.update(r.key)} remove={handlers.remove} openDetails={handlers.openDetails} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
